import { Menu } from 'lucide-react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useHeartbeat } from '../../hooks/usePresence';
import { useChatSounds } from '../../hooks/useChatSounds';
import { useDesktopBadge } from '../../hooks/useDesktopBadge';
import { useUploadLimit } from '../../hooks/useUploadLimit';
import { useModules } from '../../hooks/useModules';
import { moduleForPath } from '../../lib/moduleRoutes';
import { VoiceRoomProvider } from '../../contexts/VoiceRoomContext';
import { ErrorBoundary } from '../ErrorBoundary';
import { BugReportWidget } from '../bugReports/BugReportWidget';
import { ChatWidget } from '../teamChat/ChatWidget';
import { VoiceDock } from '../teamChat/VoiceDock';
import { TimerBar } from '../timers/TimerBar';
import { ReminderBar } from '../reminders/ReminderBar';
import { DailyStatusBar } from '../dailyStatus/DailyStatusBar';
import { BrandHeader } from './BrandHeader';
import { Sidebar } from './Sidebar';
import { SidebarProvider, useSidebar } from './SidebarContext';

function MobileTopBar() {
  const { isMobile, openMobile } = useSidebar();
  if (!isMobile) return null;

  return (
    <header className="sticky top-0 z-30 flex items-center gap-3 border-b border-sidebar-border bg-sidebar px-4 py-3 pt-[calc(0.75rem+env(safe-area-inset-top))] md:hidden">
      {/* The negative margins claw back the padding that the 44px touch target
          adds around each icon (index.css, `pointer: coarse`), so both glyphs
          sit flush with the `px-4` content edge instead of floating inset —
          and `justify-center` keeps them centred inside that target. */}
      <button
        type="button"
        onClick={openMobile}
        aria-label="Deschide meniul"
        className="-ml-1.5 inline-flex items-center justify-center rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
      >
        <Menu size={22} />
      </button>
      <BrandHeader size="sm" />
      {/* No theme toggle here: on a phone it lives in the side menu only. */}
    </header>
  );
}

function LayoutShell() {
  // The shell always locks to the viewport height so the sidebar stays put and
  // only the page content (`<main>`) scrolls. The chat page (AI Assistant) is
  // special (CLE-120): it uses the keyboard-aware `.chat-shell` height and a
  // non-scrolling main, so only its message list scrolls.
  const { pathname } = useLocation();
  const isChat = pathname.startsWith('/chat') || pathname.startsWith('/team-chat');

  // Presence heartbeat lives here, once for the whole app: you count as online
  // wherever you are, not only while /team-chat happens to be mounted.
  useHeartbeat();

  // Same reasoning for the sounds: a message arriving matters most when you
  // are somewhere else in the product, not when the chat is already open.
  useChatSounds();
  // The unread count on the desktop app's icon (no-op in a browser).
  useDesktopBadge();
  // Every upload checks files against the admin-set limit (Settings → Drive).
  useUploadLimit();

  // A page of a module that is off (Firmio Console / Settings → Module): back
  // to the start page, whose own redirect picks a page that exists.
  const modules = useModules();
  const required = moduleForPath(pathname);
  const blocked = required !== null && !modules.isLoading && !modules.isOn(required);

  return (
    <div
      className={
        isChat
          ? 'chat-shell flex overflow-hidden bg-background text-foreground'
          : 'flex h-dvh overflow-hidden bg-background text-foreground'
      }
    >
      <Sidebar />
      <div className="flex min-w-0 flex-1 flex-col">
        <MobileTopBar />
        {/* Sits in the content column, above the scroll container: it pushes the
            page down instead of covering it, and needs no sticky/z-index (which
            would create a pointless stacking context). Its own ErrorBoundary is
            mandatory — the one below wraps only <Outlet />, so a throw here would
            white-screen EVERY page, exactly what that boundary exists to prevent. */}
        <ErrorBoundary resetKey="timer-bar">
          <TimerBar />
        </ErrorBoundary>
        <ErrorBoundary resetKey="reminder-bar">
          <ReminderBar />
        </ErrorBoundary>
        {/* End-of-day check-in nudge (from the configured reminder time, while
            tasks still have no status). Same column, same boundary rule. */}
        <ErrorBoundary resetKey="daily-status-bar">
          <DailyStatusBar />
        </ErrorBoundary>
        {/* Phones only: on desktop the call lives in the sidebar's bottom-left
            panel (VoiceSidebarPanel), but on a phone that sidebar is a drawer,
            and a hang-up button behind a drawer is no hang-up button. Renders
            null unless you're in a voice channel. Same column, same boundary
            rule as the two bars above. */}
        <ErrorBoundary resetKey="voice-dock">
          <div className="md:hidden">
            <VoiceDock />
          </div>
        </ErrorBoundary>
        <main
          className={
            isChat
              ? 'flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden'
              : 'min-h-0 min-w-0 flex-1 overflow-y-auto'
          }
        >
          {/* CLE-274: isolate per-page render errors so one broken route (e.g. a
              task-detail crash) shows a recoverable fallback instead of
              white-screening the whole app. Reset on navigation via pathname. */}
          <ErrorBoundary resetKey={pathname}>
            {blocked ? <Navigate to="/" replace /> : <Outlet />}
          </ErrorBoundary>
        </main>
      </div>
      {/* "Report a bug" panel, opened from the sidebar, on every authenticated page. It lives here rather than
          at App level so it never appears on the login screens, and it is fixed
          to the viewport rather than in the content column — it must float over
          the page you are reporting on, not push it down. Own boundary, same
          rule as the bars above: a throw here would white-screen everything. */}
      <ErrorBoundary resetKey="bug-report-widget">
        <BugReportWidget />
      </ErrorBoundary>
      {/* Team chat in the corner (BUG-10). Same
          reasons for living here and for its own boundary. */}
      <ErrorBoundary resetKey="chat-widget">
        <ChatWidget />
      </ErrorBoundary>
    </div>
  );
}

/**
 * Application shell that wraps all authenticated pages with the CCC sidebar
 * navigation. Provides sidebar state (width, collapse, mobile overlay) to the
 * whole subtree.
 */
export function Layout() {
  return (
    <SidebarProvider>
      {/* Above <Outlet />, deliberately: the LiveKit Room lives in here, and a
          provider mounted inside the conversation route would be torn down —
          hanging the call up — every time you clicked another conversation. */}
      <VoiceRoomProvider>
        <LayoutShell />
      </VoiceRoomProvider>
    </SidebarProvider>
  );
}
